// src/pages/DealerVehicleListing.tsx

import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  CircularProgress,
  Typography,
  Grid,
  Stack,
  Pagination,
  Button,
  Tooltip,
  Container,
  Divider,
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useState, useEffect } from 'react';
import NotFoundPage from './NotFoundPage';
import CarCard from '../components/CarCard';
import CarFilterBar from '../components/CarFilterBar';
import axiosInstance from '../services/axiosInstance';
import OtherBranchDialog from '../components/OtherBranchDialog';

// --- Icons ---
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FaxIcon from '@mui/icons-material/Print';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import SmartphoneIcon from '@mui/icons-material/Smartphone';

// ✅ Import the decoder
import { decodeId } from '../utils/idEncoder';

interface HomePageProps {
  mode: 'light' | 'dark';
  setMode: (mode: 'light' | 'dark') => void;
}

const DealerVehicleListing = ({ mode, setMode }: HomePageProps) => {
  // 1. Rename raw URL param to hashId
  const { id: hashId } = useParams<{ id: string }>();
  
  // 2. Decode it immediately
  const realDealerId = hashId ? decodeId(hashId) : null;

  const location = useLocation();
  const navigate = useNavigate();

  // Search params logic...
  const searchParams = new URLSearchParams(location.search);
  const parseQueryParam = (key: string): number[] => {
    const raw = searchParams.get(key);
    return raw ? raw.split(',').map(Number) : [];
  };
  const vehicleTransmissionTypeIds = parseQueryParam('vehicleTransmissionTypeIds');
  const vehicleFuelTypeIds = parseQueryParam('vehicleFuelTypeIds');
  const vehicleBrandIds = parseQueryParam('vehicleBrandIds');
  const vehicleBodyTypeIds = parseQueryParam('vehicleBodyTypeIds');
  const vehicleClassIds = parseQueryParam('vehicleClassIds');
  const statusIdString = searchParams.get('statusId');
  const statusID = statusIdString ? statusIdString.split(',') : [];
  
  const parsePriceParams = () => {
    const min = searchParams.get('minAskingPrice');
    const max = searchParams.get('maxAskingPrice');
    return {
      minAskingPrice: min ? Number(min) : null,
      maxAskingPrice: max ? Number(max) : null,
    };
  };
  const { minAskingPrice, maxAskingPrice } = parsePriceParams();

  const parseYearParams = () => {
    const min = searchParams.get('minManufacturingYear');
    const max = searchParams.get('maxManufacturingYear');
    return {
      minManufacturingYear: min ? Number(min) : null,
      maxManufacturingYear: max ? Number(max) : null,
    };
  };
  const { minManufacturingYear, maxManufacturingYear } = parseYearParams();

  const parseMileageParams = () => {
    const min = searchParams.get('minMileage');
    const max = searchParams.get('maxMileage');
    return {
      minMileage: min ? Number(min) : null,
      maxMileage: max ? Number(max) : null,
    };
  };
  const { minMileage, maxMileage } = parseMileageParams();

  const parseDepreciationParams = () => {
    const min = searchParams.get('minDepreciation');
    const max = searchParams.get('maxDepreciation');
    return {
      minDepreciationValue: min ? Number(min) : null,
      maxDepreciationValue: max ? Number(max) : null,
    };
  };
  const { minDepreciationValue, maxDepreciationValue } = parseDepreciationParams();

  const vehicleModel = searchParams.get('vehicleModel') || '';

  // State
  const [vehicle, setVehicleList] = useState<any[] | null>(null);
  const [vehicleDealerInfo, setDealerInfo] = useState<any | null>(null);
  const [otherBranch, setOtherBranch] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [branchDialogOpen, setBranchDialogOpen] = useState(false);

  // ✅ Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 10;

  // 3. Update Payload to use realDealerId AND Backend Pagination
  const payload = {
    dealerId: realDealerId,
    vehicleTransmissionTypeIds,
    vehicleFuelTypeIds,
    vehicleBrandIds,
    vehicleClassIds,
    vehicleBodyTypeIds,
    minAskingPrice,
    maxAskingPrice,
    minManufacturingYear,
    maxManufacturingYear,
    minMileage,
    maxMileage,
    minDepreciationValue,
    maxDepreciationValue,
    statusID,
    // Backend Pagination & Search parameters added
    pageNumber: currentPage,
    pageSize: itemsPerPage,
    searchKeyword: searchQuery, 
    vehicleModel: vehicleModel 
  };

  const fetchVehicle = async () => {
    if (!realDealerId) return;

    try {
      setLoading(true);
      const response = await axiosInstance.post(
        '/CustomerVehicle/VehicleGetListPublished',
        payload
      );
      const data = response.data as {
        responseData?: any;
      };
      
      const responseData = data.responseData;
      setVehicleList(responseData?.vehicleList || responseData?.VehicleList || []);
      
      // ✅ Map new backend pagination data safely
      setTotalRecords(responseData?.totalRecords ?? responseData?.TotalRecords ?? 0);
      setTotalPages(responseData?.totalPages ?? responseData?.TotalPages ?? 0);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch vehicle data.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDealer = async () => {
    if (!realDealerId) return;

    try {
      const response = await axiosInstance.get(
        `/CustomerVehicle/DealerGetByCompany/${realDealerId}`
      );
      const data = response.data as {
        responseData?: any;
      };
      const branchList = data.responseData?.otherBranch || [];
      setDealerInfo(data.responseData || null);
      setOtherBranch(branchList);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch dealer information.');
    }
  };

  // ✅ Fetch data when parameters change
  useEffect(() => {
    if (realDealerId) {
      fetchVehicle();
      
      // Only fetch the dealer info once to prevent screen flashing
      if (!vehicleDealerInfo) {
        fetchDealer();
      }
    } else {
        setLoading(false);
        setError("Invalid Dealer Link");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, hashId, currentPage, itemsPerPage, searchQuery]); 

  // ✅ Reset to page 1 if search query or url params change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, location.search]);

  if (error) return <NotFoundPage />;

  const handleFilterChange = (key: string, value: string) => {
    const currentParams = new URLSearchParams(location.search);
    const existing = currentParams.get(key);
    let updatedValues: string[] = [];
    if (existing) {
      const values = existing.split(',');
      if (values.includes(value)) {
        updatedValues = values.filter((v) => v !== value);
      } else {
        updatedValues = [...values, value];
      }
      if (updatedValues.length > 0) {
        currentParams.set(key, updatedValues.join(','));
      } else {
        currentParams.delete(key);
      }
    } else {
      updatedValues = [value];
      currentParams.set(key, value);
    }
    currentParams.delete('page');
    navigate(`/dealer-vehicles/${hashId}${currentParams.toString() ? `?${currentParams.toString()}` : ''}`);
  };

// ✅ Fixed code
const googleMapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(
  vehicleDealerInfo?.address || ''
)}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

  const handleGmailOpen = (email: string) => {
    const subject = encodeURIComponent('Enquiry from Work Horse website');
    const body = encodeURIComponent(
      `Hi,\n\nI'm interested in one of the vehicles listed on the Work Horse website.\n\nCould you please provide more details or arrange for a viewing?\n\nThank you.`
    );
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;
    window.open(gmailUrl, '_blank');
  };

  const ContactRow = ({ icon, text, label, onClick, link }: any) => (
    <Tooltip title={label} arrow placement="bottom-start">
        <Box 
            display="flex" 
            alignItems="center" 
            gap={2} 
            sx={{ 
                opacity: 0.9, 
                width: 'fit-content', 
                cursor: (onClick || link) ? 'pointer' : 'default',
                '&:hover': { opacity: 1, color: 'primary.main' }
            }}
            onClick={onClick}
        >
            <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 400 }}>
                {text}
            </Typography>
        </Box>
    </Tooltip>
  );

  // Pagination Helper Display Math
  const indexOfFirstRecord = (currentPage - 1) * itemsPerPage + 1;
  const indexOfLastRecord = Math.min(currentPage * itemsPerPage, totalRecords);

  return (
    <Box sx={{ bgcolor: mode === 'dark' ? 'background.default' : '#f4f6f8', minHeight: '100vh' }}>
      <Header />
      
      <Container maxWidth="xl" sx={{ py: 8 }}>
        {loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Paper>
        ) : (
          <Box>
            {vehicleDealerInfo && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  mb: 6,
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: mode === 'dark' ? '#1e1e1e' : '#fff',
                  boxShadow: '0px 4px 20px rgba(0,0,0,0.05)'
                }}
              >
                <Grid container spacing={6}>
                  <Grid size={{ xs: 12, md: 5 }} >
                    <Box display="flex" flexDirection="column" gap={3}>
                      
                      {/* Dealer Identity */}
                      <Box display="flex" alignItems="center" gap={3}>
                        {vehicleDealerInfo.imageUrl && (
                          <Box
                            component="img"
                            src={vehicleDealerInfo.imageUrl}
                            alt={vehicleDealerInfo.dealerName}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 2,
                              objectFit: 'contain',
                              border: '1px solid',
                              borderColor: 'divider',
                              p: 1
                            }}
                          />
                        )}
                        <Box>
                            <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1.2 }}>
                                {vehicleDealerInfo.dealerName}
                            </Typography>
                        </Box>
                      </Box>

                      <Divider />

                      {/* Contact Details */}
                      <Stack spacing={2}>
                        {vehicleDealerInfo.address && (
                            <ContactRow 
                                icon={<LocationOnIcon />} 
                                text={vehicleDealerInfo.address} 
                                label="Showroom Address" 
                            />
                        )}

                        {otherBranch?.length > 0 && (
                             <Tooltip title="View other showroom locations" arrow placement="bottom-start">
                                <Button 
                                    startIcon={<StorefrontIcon />} 
                                    onClick={() => setBranchDialogOpen(true)}
                                    sx={{ justifyContent: 'flex-start', pl: 0 }}
                                >
                                    View {otherBranch.length} other branch{otherBranch.length > 1 ? 'es' : ''}
                                </Button>
                             </Tooltip>
                        )}
                        <OtherBranchDialog
                          branchDialogOpen={branchDialogOpen}
                          setBranchDialogOpen={setBranchDialogOpen}
                          otherBranch={otherBranch}
                          vehicleDealerInfo={vehicleDealerInfo}
                        />

                        {vehicleDealerInfo.officeNumber && (
                           <ContactRow 
                                icon={<BusinessIcon />} 
                                text={vehicleDealerInfo.officeNumber} 
                                label="Office Number" 
                           />
                        )}

                        {vehicleDealerInfo.faxNumber && (
                           <ContactRow 
                                icon={<FaxIcon />} 
                                text={vehicleDealerInfo.faxNumber} 
                                label="Fax Number" 
                           />
                        )}

                        {vehicleDealerInfo.contactNumber && (
                           <a href={`tel:${vehicleDealerInfo.contactNumber}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                                <ContactRow 
                                    icon={<SmartphoneIcon />} 
                                    text={vehicleDealerInfo.contactNumber} 
                                    label="Direct Contact (Click to Call)"
                                    link
                                />
                           </a>
                        )}

                        {vehicleDealerInfo.website && (
                            <a href={vehicleDealerInfo.website} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                                <ContactRow 
                                    icon={<LanguageIcon />} 
                                    text={vehicleDealerInfo.website} 
                                    label="Visit Website"
                                    link
                                />
                            </a>
                        )}

                        {vehicleDealerInfo.dealerEmail && (
                            <ContactRow 
                                icon={<EmailIcon />} 
                                text={vehicleDealerInfo.dealerEmail} 
                                label="Click to Email"
                                onClick={() => handleGmailOpen(vehicleDealerInfo.dealerEmail)}
                            />
                        )}
                      </Stack>

                      {/* Primary Action Button */}
                      {vehicleDealerInfo.dealerEmail && (
                        <Box mt={1}>
                          <Tooltip title="Send an email enquiry directly to this dealer" arrow placement="bottom">
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<EmailIcon />}
                                onClick={() => handleGmailOpen(vehicleDealerInfo.dealerEmail)}
                                sx={{ 
                                    borderRadius: 2, 
                                    textTransform: 'none', 
                                    fontWeight: 600,
                                    px: 4,
                                    py: 1.5
                                }}
                            >
                                Contact Seller
                            </Button>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Map Section */}
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Paper 
                        elevation={0}
                        sx={{ 
                            borderRadius: 4, 
                            overflow: 'hidden', 
                            height: '100%', 
                            minHeight: 350,
                            border: '1px solid',
                            borderColor: 'divider'
                        }}
                    >
                      <iframe
                        title="Dealer Location Map"
                        width="100%"
                        height="100%"
                        style={{ border: 0, minHeight: '350px' }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={googleMapSrc}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Filter Section Header */}
            <Box mb={4}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Available Inventory
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={3}>
                 Browse vehicles currently listed by {vehicleDealerInfo?.dealerName || 'this dealer'}.
              </Typography>
              
              {/* ✅ Added Total Vehicles Counter */}
              <Typography 
                variant="h6" 
                fontWeight="800" 
                color="text.primary"
                sx={{ mb: 2 }}
              >
                {totalRecords} {totalRecords === 1 ? 'Vehicle' : 'Vehicles'} Found
              </Typography>

              <CarFilterBar
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                url={`/dealer-vehicles/${hashId}`} // ✅ Use hashId for URL preservation
                onFilterChange={handleFilterChange}
              />
            </Box>

            {/* Listing Section */}
            {vehicle && vehicle.length > 0 ? (
              <>
                <Stack spacing={3}>
                  {vehicle.map((v) => (
                    <CarCard key={v.vehicleId} vehicle={v} viewMode="list" />
                  ))}
                </Stack>

                <Box mt={6} display="flex" flexDirection="column" alignItems="center">
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Showing {indexOfFirstRecord} to {indexOfLastRecord} of {totalRecords} Vehicles
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                    size="large"
                    shape="rounded"
                  />
                </Box>
              </>
            ) : (
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 10,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 4,
                  bgcolor: 'transparent',
                }}
              >
                <Typography variant="h6" fontWeight={600} gutterBottom color="text.primary">
                  No vehicles found
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={3} maxWidth={400} textAlign="center">
                  This dealer currently has no vehicles listed matching your criteria. Try adjusting your filters or check back later.
                </Typography>
                <Button 
                    variant="outlined" 
                    onClick={() => navigate('/vehicle-listing')}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  View All Listings
                </Button>
              </Paper>
            )}
          </Box>
        )}
      </Container>
      <Footer />
    </Box>
  );
};

export default DealerVehicleListing;